import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Play, X, ExternalLink } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { imgUrl, imgUrlW } from "@/lib/api";
import { LuxeMonogramMark } from "@/components/luxe/LuxeMonogramMark";
import { TikTokIcon, InstagramIcon } from "@/components/luxe/icons";

const itemType = (it) => it.type || "image";
const cleanTitle = (t = "") => t.replace(/#\S+/g, "").replace(/\s+/g, " ").trim();

const CARD_W = "w-[72vw] max-w-[300px] sm:w-[280px] lg:w-[300px]";

const ArrowButton = ({ dir, disabled, onClick, testId }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    disabled={disabled}
    aria-label={dir < 0 ? "Previous" : "Next"}
    className="flex h-11 w-11 items-center justify-center rounded-full border border-border bg-card text-foreground shadow-[var(--shadow-hairline)] transition-colors duration-300 hover:border-primary hover:bg-primary hover:text-primary-foreground disabled:pointer-events-none disabled:opacity-35"
  >
    {dir < 0 ? <ChevronLeft className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
  </button>
);

const VideoCard = ({ it, index, playing, onPlay, registerRef }) => {
  const ref = useRef(null);
  const [near, setNear] = useState(false);
  useEffect(() => registerRef(index, ref.current), [index, registerRef]);
  // Only fetch the poster once the card approaches the viewport (keeps the hero fast).
  useEffect(() => {
    const el = ref.current;
    if (!el || near) return undefined;
    if (typeof IntersectionObserver === "undefined") {
      setNear(true);
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setNear(true);
          io.disconnect();
        }
      },
      { rootMargin: "600px 600px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [near]);
  return (
    <div className="relative h-full w-full">
      <video
        ref={ref}
        data-testid={`moments-video-${index}`}
        src={`${imgUrl(it.url)}#t=0.1`}
        poster={it.thumbnail && near ? imgUrlW(it.thumbnail, 480) : undefined}
        preload={it.thumbnail ? "none" : "metadata"}
        playsInline
        controls={playing}
        onEnded={() => onPlay(-1)}
        className="h-full w-full bg-[hsl(345_45%_14%)] object-cover"
      />
      {!playing && (
        <button
          type="button"
          data-testid={`moments-play-${index}`}
          onClick={() => onPlay(index)}
          aria-label="Play video"
          className="group absolute inset-0 flex items-center justify-center"
        >
          <span className="absolute inset-0 bg-[linear-gradient(to_top,rgba(40,8,20,0.75),rgba(40,8,20,0.05)_55%)]" />
          <span className="relative flex h-16 w-16 items-center justify-center rounded-full border border-[hsl(350_60%_97%/0.8)] bg-[hsl(345_55%_12%/0.45)] text-[hsl(350_60%_97%)] backdrop-blur-sm transition-transform duration-500 group-hover:scale-110">
            <Play className="ml-1 h-6 w-6 fill-current" />
          </span>
        </button>
      )}
    </div>
  );
};

export const MomentsCarousel = ({ items = [], monogram = "SS" }) => {
  const scroller = useRef(null);
  const videoRefs = useRef({});
  const [active, setActive] = useState(0);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);
  const [playing, setPlaying] = useState(-1);
  const [lightbox, setLightbox] = useState(null); // {type, item}
  const [embedLoaded, setEmbedLoaded] = useState(false);

  const isEmpty = items.length === 0;
  const count = isEmpty ? 4 : items.length;

  const registerRef = useCallback((i, el) => {
    videoRefs.current[i] = el;
  }, []);

  const update = useCallback(() => {
    const el = scroller.current;
    if (!el) return;
    const first = el.querySelector("[data-card]");
    const step = first ? first.getBoundingClientRect().width + 16 : 1;
    setActive(Math.min(count - 1, Math.round(el.scrollLeft / step)));
    setAtStart(el.scrollLeft <= 4);
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 4);
  }, [count]);

  useEffect(() => {
    update();
    const el = scroller.current;
    if (!el) return;
    el.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      el.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [update]);

  const scrollToIndex = (i) => {
    const el = scroller.current;
    const cards = el?.querySelectorAll("[data-card]");
    if (!cards || !cards[i]) return;
    const pad = parseFloat(getComputedStyle(el).scrollPaddingLeft) || 0;
    const left = el.scrollLeft + cards[i].getBoundingClientRect().left - el.getBoundingClientRect().left - pad;
    el.scrollTo({ left, behavior: "smooth" });
  };

  const go = (dir) => scrollToIndex(Math.max(0, Math.min(count - 1, active + dir)));

  const play = (i) => {
    Object.entries(videoRefs.current).forEach(([k, v]) => {
      if (v && Number(k) !== i) v.pause();
    });
    setPlaying(i);
    if (i >= 0) {
      const v = videoRefs.current[i];
      if (v) {
        v.currentTime = v.currentTime < 0.2 ? 0 : v.currentTime;
        v.play().catch(() => {});
      }
    }
  };

  // pause when the playing card scrolls far out of view
  useEffect(() => {
    if (playing >= 0 && Math.abs(active - playing) > 1) play(-1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const openItem = (it, i) => {
    const t = itemType(it);
    if (t === "image") setLightbox({ type: "image", item: it });
    else if (t === "instagram") {
      setEmbedLoaded(false);
      setLightbox({ type: "instagram", item: it });
    } else if (t === "tiktok") {
      if (!it.video_id) window.open(it.url, "_blank", "noopener,noreferrer");
      else {
        setEmbedLoaded(false);
        setLightbox({ type: "tiktok", item: it });
      }
    }
  };

  return (
    <div data-testid="moments-carousel">
      <div
        ref={scroller}
        data-testid="moments-scroller"
        className="no-scrollbar -mx-5 flex snap-x snap-mandatory scroll-px-5 gap-4 overflow-x-auto px-5 pb-2 sm:-mx-8 sm:scroll-px-8 sm:px-8 lg:mx-0 lg:scroll-px-0 lg:px-0"
      >
        {isEmpty
          ? Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                data-card
                data-testid="moments-placeholder-card"
                data-luxe-noise="true"
                className={`${CARD_W} aspect-[9/16] shrink-0 snap-start overflow-hidden rounded-3xl border border-border/70 bg-[linear-gradient(160deg,hsl(348_70%_95%),hsl(345_50%_90%))]`}
              >
                <div className="relative z-[1] flex h-full flex-col items-center justify-center gap-4 opacity-70">
                  <LuxeMonogramMark letters={monogram} size="sm" />
                  <span className="eyebrow text-[9px] text-muted-foreground">Coming soon</span>
                </div>
              </div>
            ))
          : items.map((it, i) => {
              const t = itemType(it);
              const caption = cleanTitle(it.caption || it.title || "");
              return (
                <div
                  key={it.id || i}
                  data-card
                  data-testid={`moments-card-${i}`}
                  data-type={t}
                  className={`${CARD_W} group relative aspect-[9/16] shrink-0 snap-start overflow-hidden rounded-3xl border border-border/60 bg-secondary shadow-[var(--shadow-hairline)]`}
                >
                  {t === "video" ? (
                    <VideoCard it={it} index={i} playing={playing === i} onPlay={play} registerRef={registerRef} />
                  ) : (
                    <button type="button" onClick={() => openItem(it, i)} className="relative block h-full w-full text-left" aria-label={caption || `Moment ${i + 1}`}>
                      {it.thumbnail || it.url ? (
                        <img
                          src={imgUrlW(t === "image" ? it.url : it.thumbnail, 640)}
                          alt={caption || `Moment ${i + 1}`}
                          loading="lazy"
                          className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center bg-[hsl(345_45%_18%)]">
                          <TikTokIcon className="h-10 w-10 text-[hsl(345_80%_84%)]" />
                        </div>
                      )}
                      {(t === "tiktok" || t === "instagram") && (
                        <>
                          <span className="absolute inset-0 bg-[linear-gradient(to_top,rgba(40,8,20,0.75),rgba(40,8,20,0.05)_55%)]" />
                          <span className="absolute left-1/2 top-1/2 flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-[hsl(350_60%_97%/0.8)] bg-[hsl(345_55%_12%/0.45)] text-[hsl(350_60%_97%)] backdrop-blur-sm transition-transform duration-500 group-hover:scale-110">
                            <Play className="ml-1 h-6 w-6 fill-current" />
                          </span>
                          <span className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-[hsl(345_55%_12%/0.6)] text-[hsl(350_60%_97%)]">
                            {t === "tiktok" ? <TikTokIcon className="h-3.5 w-3.5" /> : <InstagramIcon className="h-3.5 w-3.5" />}
                          </span>
                        </>
                      )}
                    </button>
                  )}
                  {caption && playing !== i && (
                    <p className="pointer-events-none absolute bottom-0 left-0 right-0 bg-[linear-gradient(to_top,rgba(40,8,20,0.8),transparent)] px-5 pb-5 pt-12 text-sm font-semibold leading-snug text-[hsl(350_60%_97%)]">
                      <span className="line-clamp-2">{caption}</span>
                    </p>
                  )}
                </div>
              );
            })}
      </div>

      <div className="mt-6 flex items-center justify-between gap-4">
        <div className="-ml-2 flex items-center" data-testid="moments-dots">
          {Array.from({ length: count }).map((_, i) => (
            <button
              key={i}
              type="button"
              aria-label={`Go to ${i + 1}`}
              aria-current={i === active ? "true" : undefined}
              data-testid={`moments-dot-${i}`}
              onClick={() => scrollToIndex(i)}
              className="group flex h-6 min-w-6 items-center justify-center rounded-full px-[3px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <span
                className={`block h-1.5 rounded-full transition-[width,background-color] duration-300 ${i === active ? "w-6 bg-primary" : "w-1.5 bg-[hsl(345_35%_80%)] group-hover:bg-[hsl(345_35%_65%)]"}`}
              />
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <ArrowButton dir={-1} disabled={atStart} onClick={() => go(-1)} testId="moments-prev-button" />
          <ArrowButton dir={1} disabled={atEnd} onClick={() => go(1)} testId="moments-next-button" />
        </div>
      </div>

      <Dialog open={!!lightbox} onOpenChange={(v) => !v && setLightbox(null)}>
        <DialogContent
          data-testid="moments-dialog"
          className={`on-dark gap-0 overflow-hidden border-none bg-[hsl(345_55%_9%)] p-0 text-[hsl(350_60%_97%)] sm:rounded-2xl [&>button]:hidden ${
            lightbox?.type === "tiktok" || lightbox?.type === "instagram" ? "w-[min(92vw,400px)] max-w-none" : "max-w-[min(92vw,820px)]"
          }`}
        >
          <DialogTitle className="sr-only">{lightbox?.item?.caption || lightbox?.item?.title || "Moment"}</DialogTitle>
          <DialogDescription className="sr-only">Moment viewer</DialogDescription>
          <div className="flex h-12 items-center justify-between border-b border-[hsl(345_35%_40%/0.4)] pl-5 pr-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.24em] text-[hsl(345_40%_86%)]">
              {lightbox?.type === "tiktok" ? "TikTok" : lightbox?.type === "instagram" ? "Instagram" : "Moment"}
            </span>
            <button
              type="button"
              data-testid="moments-dialog-close"
              onClick={() => setLightbox(null)}
              aria-label="Close"
              className="flex h-9 w-9 items-center justify-center rounded-full text-[hsl(350_60%_97%)] transition-colors hover:bg-[hsl(345_45%_20%)]"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          {lightbox?.type === "image" && (
            <img src={imgUrl(lightbox.item.url)} alt={lightbox.item.caption || "Moment"} className="max-h-[82vh] w-full object-contain" />
          )}
          {lightbox?.type === "tiktok" && (
            <>
              <div className="relative h-[min(72vh,680px)] w-full">
                {!embedLoaded && <Skeleton className="absolute inset-0 rounded-none bg-[hsl(345_40%_18%)]" />}
                <iframe
                  title={cleanTitle(lightbox.item.title) || "TikTok"}
                  src={`https://www.tiktok.com/embed/v2/${lightbox.item.video_id}`}
                  className="h-full w-full"
                  allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
                  allowFullScreen
                  onLoad={() => setEmbedLoaded(true)}
                />
              </div>
              <a
                href={lightbox.item.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 border-t border-[hsl(345_35%_40%/0.4)] px-5 py-3.5 text-xs uppercase tracking-[0.24em] text-[hsl(345_40%_86%)] transition-colors hover:text-[hsl(var(--gold))]"
              >
                Open in TikTok <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </>
          )}
          {lightbox?.type === "instagram" && (
            <>
              <div className="relative h-[min(78vh,720px)] w-full bg-white">
                {!embedLoaded && <Skeleton className="absolute inset-0 rounded-none bg-[hsl(345_40%_18%)]" />}
                <iframe
                  title="Instagram"
                  src={`https://www.instagram.com/p/${lightbox.item.ig_code}/embed/`}
                  className="h-full w-full"
                  allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
                  allowFullScreen
                  onLoad={() => setEmbedLoaded(true)}
                />
              </div>
              <a
                href={lightbox.item.source_url || lightbox.item.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 border-t border-[hsl(345_35%_40%/0.4)] px-5 py-3.5 text-xs uppercase tracking-[0.24em] text-[hsl(345_40%_86%)] transition-colors hover:text-[hsl(var(--gold))]"
              >
                Open in Instagram <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </>
          )}

        </DialogContent>
      </Dialog>
    </div>
  );
};
